import { ApiError, RequestTimeoutError } from '@/core/api/http'
import {
  verbisLebenslaufErrorBody,
  type VerbisLebenslaufErrorBody,
  type VerbisLebenslaufZuordnung,
} from '../../api/verbisLebenslauf'

const NETWORK_FALLBACK_TEXT =
  "Hochladen fehlgeschlagen. Bitte prüfen Sie Ihre Internetverbindung und versuchen Sie es erneut."

const MALFORMED_RESPONSE_TEXT = "Die Antwort des Servers hatte nicht die erwartete Form."

export type VerbisLebenslaufUploadFailure = (
  | { kind: 'toast'; message: string }
  | { kind: 'prompt'; body: VerbisLebenslaufErrorBody }
) & { reload: boolean; zuordnung: VerbisLebenslaufZuordnung[] }

export function verbisLebenslaufStatusFailureMessage(error: unknown): string {
  const body = verbisLebenslaufErrorBody(error)
  if (body) return body.meldung
  if (error instanceof ApiError) {
    return `Der Stand konnte nicht geladen werden (Fehler ${error.status}). Bitte versuchen Sie es später erneut.`
  }
  const networkText =
    "Der Stand konnte nicht geladen werden. Bitte prüfen Sie Ihre Internetverbindung und versuchen Sie es erneut."
  if (error instanceof RequestTimeoutError) return error.message
  if (error instanceof TypeError) return networkText
  return MALFORMED_RESPONSE_TEXT
}

export function classifyVerbisLebenslaufUploadFailure(error: unknown): VerbisLebenslaufUploadFailure {
  if (error instanceof ApiError) {
    const reload = error.status === 409
    const body = verbisLebenslaufErrorBody(error)
    if (body) {
      const { zuordnung } = body
      return body.fehler.length === 0
        ? { kind: 'toast', message: body.meldung, reload, zuordnung }
        : { kind: 'prompt', body, reload, zuordnung }
    }
    return {
      kind: 'toast',
      message: `Hochladen fehlgeschlagen (Fehler ${error.status}). Bitte später erneut versuchen.`,
      reload,
      zuordnung: [],
    }
  }
  if (error instanceof RequestTimeoutError) {
    return { kind: 'toast', message: error.message, reload: false, zuordnung: [] }
  }
  if (error instanceof TypeError) {
    return { kind: 'toast', message: NETWORK_FALLBACK_TEXT, reload: false, zuordnung: [] }
  }
  return {
    kind: 'toast',
    message: MALFORMED_RESPONSE_TEXT,
    reload: false,
    zuordnung: [],
  }
}

export type LebenslaufZeitraumFailure = {
  message: string
  felder: { von?: string; bis?: string }
  reload: boolean
}

export function classifyLebenslaufZeitraumFailure(error: unknown): LebenslaufZeitraumFailure {
  const body = verbisLebenslaufErrorBody(error)
  if (body) {
    const felder: LebenslaufZeitraumFailure['felder'] = {}
    for (const zeile of body.fehler) {
      if (zeile.feld === 'von' || zeile.feld === 'bis') felder[zeile.feld] = zeile.grund
    }
    return { message: body.meldung, felder, reload: error instanceof ApiError && error.status === 409 }
  }
  if (error instanceof ApiError) {
    return {
      message: `Das Datum konnte nicht gespeichert werden (Fehler ${error.status}). Bitte versuchen Sie es später erneut.`,
      felder: {},
      reload: error.status === 409,
    }
  }
  if (error instanceof RequestTimeoutError) return { message: error.message, felder: {}, reload: false }
  if (error instanceof TypeError) {
    return {
      message:
        "Das Datum konnte nicht gespeichert werden. Bitte prüfen Sie Ihre Internetverbindung und versuchen Sie es erneut.",
      felder: {},
      reload: false,
    }
  }
  return { message: MALFORMED_RESPONSE_TEXT, felder: {}, reload: false }
}

export type VerbisUeberspringenFailure = { message: string; reload: boolean }

export function classifyVerbisUeberspringenFailure(error: unknown): VerbisUeberspringenFailure {
  const body = verbisLebenslaufErrorBody(error)
  const reload = error instanceof ApiError && error.status === 409
  if (body) return { message: body.meldung, reload }
  if (error instanceof ApiError) {
    return {
      message: `Die Person konnte nicht weitergeschoben werden (Fehler ${error.status}). Bitte versuchen Sie es später erneut.`,
      reload,
    }
  }
  if (error instanceof RequestTimeoutError) return { message: error.message, reload: false }
  if (error instanceof TypeError) {
    return {
      message:
        "Die Person konnte nicht weitergeschoben werden. Bitte prüfen Sie Ihre Internetverbindung und versuchen Sie es erneut.",
      reload: false,
    }
  }
  return { message: MALFORMED_RESPONSE_TEXT, reload: false }
}
